"use client";

import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { driver, type DriveStep, type Config } from "driver.js";
import "driver.js/dist/driver.css";

const STORAGE_KEY = "rekkd-onboarding-done";

/** Dispatch this event from anywhere to re-open the wizard. */
export const REPLAY_TOUR_EVENT = "rekkd:replay-tour";

export function replayTour() {
  window.dispatchEvent(new Event(REPLAY_TOUR_EVENT));
}

const TOUR_STEPS: DriveStep[] = [
  {
    popover: {
      title: "Welcome to Rekkd",
      description:
        "We check whether ChatGPT, Gemini, Claude, and Perplexity actually recommend your restaurant — and show you exactly what to fix. Let's take a quick tour.",
    },
  },
  {
    element: "[data-tour='metrics-zone']",
    popover: {
      title: "Portfolio overview",
      description:
        "Average score and grade across the places you follow, plus anything actively in flight. Your score trend over time sits below it.",
    },
  },
  {
    element: "[data-tour='insights-zone']",
    popover: {
      title: "Latest insights",
      description:
        "Score drift, new competitors, and sentiment shifts surface here as Rekkd re-checks your followed places.",
    },
  },
  {
    element: "[data-tour='tasks-zone']",
    popover: {
      title: "Top tasks",
      description:
        "Action items from your reports become tasks here. Drag to reorder, click to open, and check them off as you ship.",
    },
  },
  {
    element: "[data-tour='following-zone']",
    popover: {
      title: "Top places",
      description:
        "Your top followed places at a glance — score, grade, and recent change. Click through to the full report.",
    },
  },
  {
    element: "[data-tour='tools-zone']",
    popover: {
      title: "Free tools",
      description:
        "Utilities you can point at any restaurant — start with the Schema.org generator to make your site easier for AI to parse.",
    },
  },
  {
    element: "[data-tour='nav-places']",
    popover: {
      title: "My Places",
      description:
        "Every place you've checked lives here. Follow one to track its AI visibility over time.",
    },
  },
  {
    element: "[data-tour='nav-avatar']",
    popover: {
      title: "Your account",
      description:
        "Manage your plan, view billing, and replay this tour anytime from the menu.",
    },
  },
  {
    element: "[data-tour='new-check-btn']",
    popover: {
      title: "Run your first check",
      description:
        "Ready? Click here to paste a Google Maps link. It takes about 2 minutes and we'll notify you when it's done.",
    },
  },
];

function isElementVisible(el: Element | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function resolveSteps(steps: DriveStep[]): DriveStep[] {
  return steps.filter((step) => {
    if (!step.element) return true;
    if (typeof step.element !== "string") return true;
    return isElementVisible(document.querySelector(step.element));
  });
}

export function OnboardingWizard() {
  const router = useRouter();
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  const startTour = useCallback(
    (isFirstTime: boolean) => {
      // Wait a tick for DOM elements to be rendered
      requestAnimationFrame(() => {
        const steps = resolveSteps(TOUR_STEPS);
        if (steps.length === 0) return;
        const lastStep = steps[steps.length - 1];
        const endsOnNewCheck =
          typeof lastStep.element === "string" &&
          lastStep.element === "[data-tour='new-check-btn']";

        const driverConfig: Config = {
          showProgress: true,
          animate: true,
          allowClose: true,
          overlayColor: "rgba(0, 0, 0, 0.6)",
          stagePadding: 8,
          stageRadius: 12,
          popoverClass: "rekkd-tour-popover",
          nextBtnText: "Next",
          prevBtnText: "Back",
          doneBtnText: endsOnNewCheck ? "Run first check →" : "Done",
          progressText: "{{current}} / {{total}}",
          steps,
          onDestroyStarted: () => {
            driverRef.current?.destroy();
            if (isFirstTime) {
              try {
                localStorage.setItem(STORAGE_KEY, "1");
              } catch {
                // localStorage unavailable
              }
            }
          },
          onDestroyed: () => {
            // Navigate to /new if the tour reached the final "run first check" step
            if (
              endsOnNewCheck &&
              driverRef.current?.getActiveIndex() === steps.length - 1
            ) {
              router.push("/new");
            }
          },
        };

        const d = driver(driverConfig);
        driverRef.current = d;
        d.drive();
      });
    },
    [router]
  );

  // Auto-start for first-time users, or replay if flagged via sessionStorage
  useEffect(() => {
    try {
      const replay = sessionStorage.getItem("rekkd-replay-tour");
      if (replay) {
        sessionStorage.removeItem("rekkd-replay-tour");
        const timeout = setTimeout(() => startTour(false), 600);
        return () => clearTimeout(timeout);
      }
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return;
    }

    const timeout = setTimeout(() => startTour(true), 600);
    return () => clearTimeout(timeout);
  }, [startTour]);

  // Listen for replay-tour event (same-page replay)
  useEffect(() => {
    const handler = () => startTour(false);
    window.addEventListener(REPLAY_TOUR_EVENT, handler);
    return () => window.removeEventListener(REPLAY_TOUR_EVENT, handler);
  }, [startTour]);

  return null;
}
